import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';

export default function SetupPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [alreadyExists, setAlreadyExists] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const [form, setForm] = useState({
    username: 'jcarrero',
    password: '',
    confirm: '',
    email: '',
    full_name: 'Super Administrador',
  });
  const [showReset, setShowReset] = useState(false);
  const [resetting, setResetting] = useState(false);

  const handleReset = async (secret) => {
    setResetting(true);
    try {
      const { data } = await axios.post('/auth/bootstrap-reset', { secret: secret.trim() });
      toast.success(`Cuenta eliminada (${data.deleted_count}). Ahora puedes crear una nueva.`);
      setShowReset(false);
      setAlreadyExists(false);
      setDone(false);
    } catch (err) {
      const s = err?.response?.status;
      if (s === 404) toast.error('El reinicio no está configurado. Contacta al desarrollador.');
      else if (s === 401) toast.error('Secreto incorrecto.');
      else toast.error(err?.response?.data?.detail || 'Error al reiniciar');
    } finally {
      setResetting(false);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get('/auth/bootstrap-status');
        setAlreadyExists(!!data.super_admin_exists);
      } catch {
        setAlreadyExists(false);
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) {
      toast.error('La contraseña debe tener al menos 8 caracteres');
      return;
    }
    if (form.password !== form.confirm) {
      toast.error('Las contraseñas no coinciden');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post('/auth/bootstrap-super-admin', {
        username: form.username.trim(),
        password: form.password,
        email: form.email.trim(),
        full_name: form.full_name.trim() || 'Super Administrador',
      });
      toast.success('¡Cuenta creada exitosamente!');
      setDone(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'No se pudo crear la cuenta';
      toast.error(msg);
      if (err?.response?.status === 403) setAlreadyExists(true);
    } finally {
      setSubmitting(false);
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-10 w-10 border-4 border-orange-500 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Verificando instalación…</p>
        </div>
      </div>
    );
  }

  if (alreadyExists || done) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-orange-50 via-white to-slate-50">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
          <div className="flex justify-center mb-4">
            <div className="h-14 w-14 rounded-2xl bg-green-100 flex items-center justify-center">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 text-center" data-testid="setup-already-done-title">
            {done ? '¡Listo!' : 'Instalación completada'}
          </h1>
          <p className="text-slate-600 text-center mt-2 mb-6">
            {done
              ? 'Tu cuenta de Super Administrador ha sido creada. Inicia sesión para continuar.'
              : 'Ya existe un Super Administrador en esta instalación. Por seguridad, esta página se ha bloqueado automáticamente.'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full py-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold transition"
            data-testid="setup-goto-login-btn"
          >
            Ir al inicio de sesión
          </button>
          {!done && (
            <>
              <button
                onClick={() => setShowReset(true)}
                className="w-full mt-3 py-2.5 rounded-lg text-sm text-slate-500 hover:text-red-600 hover:bg-red-50 transition"
                data-testid="setup-show-reset-btn"
              >
                ¿Perdiste el acceso? Reiniciar cuenta
              </button>
              {showReset && <ResetModal onClose={() => setShowReset(false)} onReset={handleReset} resetting={resetting} />}
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-gradient-to-br from-orange-50 via-white to-slate-50">
      <div className="max-w-lg w-full bg-white rounded-2xl shadow-lg p-8 border border-slate-200">
        <div className="flex justify-center mb-3">
          <img src="/logo-jk.png" alt="JK Enterprise" className="h-14 w-auto" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 text-center">Configuración inicial</h1>
        <p className="text-slate-600 text-center text-sm mt-1 mb-6">
          Crea la cuenta del Super Administrador (dueño de la plataforma)
        </p>

        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-lg p-3 mb-5">
          <strong>⚠️ Esta página se muestra una sola vez.</strong> Después de crear la cuenta, se bloqueará automáticamente por seguridad.
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Usuario *</label>
            <input
              type="text"
              required
              minLength={3}
              value={form.username}
              onChange={(e) => setForm({ ...form, username: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="ej: jcarrero"
              data-testid="setup-username-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Nombre completo</label>
            <input
              type="text"
              value={form.full_name}
              onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="ej: Jorge Carrero"
              data-testid="setup-fullname-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="tucorreo@ejemplo.com"
              data-testid="setup-email-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Contraseña *</label>
            <input
              type="password"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => setForm({ ...form, password: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Mínimo 8 caracteres"
              data-testid="setup-password-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Confirmar contraseña *</label>
            <input
              type="password"
              required
              minLength={8}
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Repite la contraseña"
              data-testid="setup-confirm-input"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full py-3 rounded-lg bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold transition"
            data-testid="setup-submit-btn"
          >
            {submitting ? 'Creando…' : 'Crear cuenta de Super Administrador'}
          </button>
        </form>

        <p className="text-xs text-slate-400 text-center mt-6">
          © 2026 JK Enterprise · EduCore®
        </p>
      </div>
    </div>
  );
}

function ResetModal({ onClose, onReset, resetting }) {
  const [secret, setSecret] = useState('');
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center px-4 z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-xl p-6 max-w-sm w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center mb-3">
          <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center">
            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
            </svg>
          </div>
        </div>
        <h2 className="text-xl font-bold text-slate-900 text-center mb-2">Reiniciar cuenta de dueño</h2>
        <p className="text-sm text-slate-600 text-center mb-4">
          Esto eliminará al Super Administrador actual para que puedas crear uno nuevo.
          Esta acción requiere el secreto de reinicio configurado en el servidor.
        </p>
        <label className="block text-sm font-medium text-slate-700 mb-1">Secreto de reinicio</label>
        <input
          type="password"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 mb-4"
          placeholder="Pega aquí el secreto del servidor"
          data-testid="setup-reset-secret-input"
          autoFocus
        />
        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={resetting}
            className="flex-1 py-2.5 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 transition"
            data-testid="setup-reset-cancel-btn"
          >
            Cancelar
          </button>
          <button
            onClick={() => onReset(secret)}
            disabled={resetting || secret.length < 16}
            className="flex-1 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white font-semibold transition"
            data-testid="setup-reset-confirm-btn"
          >
            {resetting ? 'Reiniciando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
